import css from '@/components/ui/shared/Table/Table.module.css';
import { useTableContext } from '@/components/ui/shared/Table/TableContext';
import { TypedCheckbox as Checkbox } from '@/components/ui/shared/Table/internal/TypedCheckbox';
import SimpleArrowDown from '@/styles/Icons/SimpleArrowDown';
import { classNames } from '@/utils/helpers/classNames';
import { Children, useId, useState } from 'react';
import type { ReactNode } from 'react';

const LABELS = {
  expand: 'Развернуть',
  collapse: 'Свернуть',
  selectRow: 'выбрать строку'
};

export interface RowProps {
  rowId: string;
  /** Presence renders the row's chevron; requires `expandable` on Table. */
  expanded?: ReactNode;
  /** Accessible label for this row's selection checkbox. */
  ariaLabel?: string;
  className?: string;
  children: ReactNode;
}

export function Row({ rowId, expanded, ariaLabel, className, children }: RowProps) {
  const { selectable, selectedIds, onSelectionChange, expandable } = useTableContext();
  const [isExpanded, setIsExpanded] = useState(false);
  const baseId = useId();
  const regionId = `${baseId}-expanded`;

  const isSelected = selectedIds.has(rowId);
  const hasExpandedContent = expandable && expanded != null;
  const hasLeadingColumn = selectable || expandable;

  const toggleSelected = () => {
    if (!onSelectionChange) return;
    const next = new Set(selectedIds);
    if (next.has(rowId)) {
      next.delete(rowId);
    } else {
      next.add(rowId);
    }
    onSelectionChange(next);
  };

  const totalColumns = Children.count(children) + (selectable ? 1 : 0) + (expandable ? 1 : 0);

  return (
    <>
      <div
        role="row"
        className={classNames({
          [css.row]: true,
          [css.rowExpanded]: isExpanded,
          [css.rowNoLeadingColumn]: !hasLeadingColumn,
          [className ?? '']: Boolean(className)
        })}
      >
        {selectable && (
          <div role="cell" className={css.checkboxCol}>
            <Checkbox
              id={`${baseId}-select`}
              checked={isSelected}
              onChange={toggleSelected}
              ariaLabel={ariaLabel ?? LABELS.selectRow}
            />
          </div>
        )}
        {expandable && (
          <div role="cell" className={css.expandCol}>
            {hasExpandedContent && (
              <button
                type="button"
                className={classNames({
                  [css.chevronBtn]: true,
                  [css.chevronBtnExpanded]: isExpanded
                })}
                onClick={() => setIsExpanded(v => !v)}
                aria-expanded={isExpanded}
                aria-controls={regionId}
                aria-label={isExpanded ? LABELS.collapse : LABELS.expand}
              >
                <SimpleArrowDown className={css.chevronIcon} />
              </button>
            )}
          </div>
        )}
        {children}
      </div>
      {hasExpandedContent && isExpanded && (
        <div role="row">
          <div role="cell" id={regionId} aria-colspan={totalColumns} className={css.expandedCell}>
            {expanded}
          </div>
        </div>
      )}
    </>
  );
}
